import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const messages = {
  en: {
    nav: {
      solutions: 'Solutions',
      about: 'About',
      team: 'Team',
      careers: 'Careers',
      updates: 'Updates',
      contact: 'Contact'
    },
    about: {
      slogan: 'Water Solutions, Now',
      lead: 'We build the bridge between Civil Engineering and the Modern Tech Stack.',
      paragraph2000s: "Most water resource projects are still managed using prehistoric workflows: static spreadsheets, manual GIS digitizing, and desktop software that hasn't been updated since the 2000s.",
      header: 'About Us',
      photoAlt: 'Berrik team and hydro-informatics tools',
      copy: 'Berrik is a Chilean-based engineering consultancy dedicated to proposing innovative and cutting-edge solutions for hydrological and hydraulic analysis. We combine modern solutions with traditional design principles. We have an international track record of experience in hydrological and hydraulic modeling for flood risk solutions in urban resilience. We incorporate critical factors such as climate change and statistical trends into our analyses to deliver results that account for realistic projections.'
    },
    seo: {
      homeTitle: 'Berrik | Hydraulic Engineering & Automated Modeling',
      homeDescription: 'Berrik provides advanced hydraulic and hydrology engineering services, including flood risk analysis, inundation mapping, and automated modeling with MiCuenca.',
      aboutTitle: 'About Berrik | Engineering & Hydro-Informatics',
      aboutDescription: 'Berrik combines traditional civil engineering with modern hydro-informatics. We specialize in water resources, flood risk, and automated modeling.',
      solutionsTitle: 'Services & Solutions | Berrik Engineering',
      solutionsDescription: 'Explore Berrik\'s engineering services: Hydraulic modeling, flood risk analysis, hydrodynamic loads, and our automated watershed tool, MiCuenca.',
      contactTitle: 'Contact Berrik | Hydraulic Engineering Services',
      contactDescription: 'Contact Berrik for hydraulic engineering, flood risk assessments, and water resource consulting. We operate in Chile and internationally.'
    },
    team: {
      titles: {
        founderCEO: 'Founder & CEO',
        coFounder: 'Co-Founder',
        seniorEngineer: 'Senior Engineer',
        civilDesigner: 'Civil Designer'
      },
      names: {
        founder: 'Javier Mardones, PE, MSc',
        coFounder: '-',
        seniorEngineer: '-',
        civilDesigner: '-'
      }
    },
    
    home: {
      exploreTitle: 'Explore our solutions and learn more about us',
      exploreSub: 'Discover how we combine engineering expertise and automation to deliver faster, clearer decisions.',
      exploreCtaSolutions: 'Explore our solutions',
      exploreCtaAbout: 'About us',
      projectsNewsHeader: 'Projects & News',
      projectsNewsSub: 'Our recent work and insights across hydrology and hydraulics',
      latestUpdatesHeader: 'Latest Updates',
      latestUpdatesSub: 'Announcements and platform changes to keep you informed',
      projectMicuencaTitle: 'MiCuenca (Beta)',
      projectMicuencaDesc: 'MiCuenca is available in beta version. Use it for initial watershed analysis.',
      newsBerrikTitle: 'Berrik Enters the Market',
      newsBerrikDesc: 'Berrik is new to the market, ready to operate and lead in water resources engineering.',
      newsOpenClientsTitle: 'Berrik opens doors',
      newsOpenClientsDesc: 'Contact us to learn more about how to start operating with us.',
      newsMicuencaRegionsTitle: 'MiCuenca available in Chile (Regions I–X)',
      newsMicuencaRegionsDesc: 'Coverage expanded from Region I to Region X.',
      newsHiringTitle: 'Berrik is seeking new talent',
      newsHiringDesc: 'We are hiring for water resources and data roles.',
      actionViewProjects: 'Go To MiCuenca.cl',
      actionAboutUs: 'About us',
      meetTeamText: 'Meet the Team',
      meetTeamSub: 'Multidisciplinary specialists in hydrology, hydraulics, and software engineering.',
      meetTeamCta: 'Meet the Team',
      careersText: 'Join our team to build resilient water infrastructure',
      careersSub: 'We are looking for engineers and data specialists focused on impact and innovation.',
      careersCta: 'See openings',
      actionViewService: 'View related service',
      actionReadUpdate: 'Read more',
      welcome: {
        slide0: {
          title: 'Endure. Optimize. Excel.',
          text: ''
        },
        slide1: {
          title: 'Surface Runoff Modeling',
          text: '1D/2D hydrologic and hydraulic modeling for flood analysis under extreme flows.'
        },
        slide2: {
          title: 'Web Tools for Watershed Analysis',
          text: 'Get watershed information and design flows in seconds at MiCuenca.cl.'
        },
        slide3: {
          title: 'Water Resources Engineering Studies',
          text: 'Climate change, water balances, agency-specific and environmental permits.'
        },
        slide4: {
          title: 'Hydraulic Infrastructure Design',
          text: 'Resiliency analysis, hydrodynamic loads, erosion and scour.'
        }
      }
    },
    services: {
      header: 'Hydrology & Hydraulic Services',
      viewDetails: 'View Details',
      requestProposal: 'Request a Proposal',
      solutionsCustom: {
        items: {
          mhh: {
            title: 'Hydrologic and hydraulic modeling',
            description: '1D/2D workflows, calibration, and scenarios to support design.'
          },
          avanzada: {
            title: 'Cutting‑edge water analysis solutions',
            description: 'Accessible online tools and automation to optimize resource allocation.'
          },
          riesgo: {
            title: 'Flood risk analysis',
            description: 'Hydrologic and hydraulic studies incorporating resilience to extreme events and climate change.'
          },
          permisos: {
            title: 'Environmental assessment and sector permits',
            description: 'Management of environmental regulations and sector permit processing within the current framework.'
          },
          diseno: {
            title: 'Hydraulic infrastructure design',
            description: 'Analysis of hydrodynamic loads, scour, and erosion; design of structures to withstand extreme events.'
          },
          asesoria: {
            title: 'Engineering and technology advisory services',
            description: 'Technical advisory and tailored engineering and IT solutions grounded in water resources.'
          }
        }
      }
    },
    pages: {
      solutionsHeader: 'Explore our solutions',
      updatesHeader: 'Updates',
      teamHeader: 'Meet the Team'
    },
    footer: {
      contactUs: 'Contact us',
      rights: 'All rights reserved.'
    },
    cookies: {
      text: 'We use cookies and similar technologies to understand usage and improve this site. You can accept or reject non-essential cookies.',
      accept: 'Accept',
      reject: 'Reject'
    },
    notFound: {
      title: 'Page not found',
      text: 'The page you are looking for does not exist or has been moved.',
      cta: 'Back to home'
    },
    contact: {
      header: 'Contact',
      subheader: 'Ready to discuss your project? Reach out.',
      infoTitle: 'Contact Information',
      phoneLabel: 'Phone',
      phoneUnavailable: 'Phone available on request',
      emailLabel: 'Email',
      emailResponse: 'We respond within 24 hours',
      headOfficeLabel: 'Head Office',
      officeHoursLabel: 'Office Hours',
      officeHours: '',
      formTitle: 'Write to Us',
      nameLabel: 'Name *',
      namePlaceholder: 'Your full name',
      emailFieldLabel: 'Email *',
      emailPlaceholder: 'your.email@example.com',
      companyLabel: 'Company',
      companyPlaceholder: 'Your company name',
      serviceLabel: 'Service',
      servicePlaceholder: 'Choose a service',
      serviceOptions: {
        hydraulicModeling: 'Hydrologic and hydraulic modeling',
        sectorPermits: 'Environmental assessment and sector permits',
        hydraulicInfrastructure: 'Hydraulic infrastructure design',
        engineeringConsult: 'Engineering and technology consult',
        other: 'Other'
      },
      messageLabel: 'Describe your project *',
      messagePlaceholder: 'Tell us about your project...',
      sendMessage: 'Send Message',
      sending: 'Sending...',
      rateLimitPrefix: 'You can send another message in',
      rateLimitSuffix: 'min.',
      successMessage: 'Thank you! Your message was sent successfully. We will contact you soon.',
      errors: {
        nameRequired: 'Name is required',
        emailRequired: 'Email is required',
        emailInvalid: 'Email is invalid',
        messageRequired: 'Message is required',
        captchaRequired: 'Please complete the captcha.',
        rateLimit: 'Rate limit reached. Please try again later.',
        sendFailed: 'An error occurred while sending the message.',
        network: 'Network error. Please try again.'
      }
    }
  },
  es: {
    nav: {
      solutions: 'Soluciones',
      about: 'Nosotros',
      team: 'Equipo',
      careers: 'Carreras',
      updates: 'Noticias',
      contact: 'Contacto'
    },
    about: {
      slogan: 'Soluciones Hídricas, Ahora',
      lead: 'Construimos el puente entre la ingeniería civil y el stack tecnológico moderno.',
      paragraph2000s: 'La mayoría de los proyectos de recursos hídricos aún se gestionan con flujos de trabajo prehistóricos: hojas de cálculo estáticas, digitalización manual en GIS y software de escritorio que no se actualiza desde los 2000.',
      header: 'Sobre Nosotros',
      photoAlt: 'Equipo de Berrik y herramientas hidro‑informáticas',
      copy: 'Berrik es una consultora de ingeniería con base en Chile, dedicada a desarrollar soluciones innovadoras y a la vanguardia para el análisis hidrológico e hidráulico. Nuestra propuesta integra herramientas y metodologías modernas con los principios del diseño tradicional, ofreciendo lo mejor de ambos enfoques. Contamos con una trayectoria y experiencia internacional en modelación hidrológica e hidráulica aplicada, especialmente en el desarrollo de soluciones para la gestión del riesgo de inundaciones y el fortalecimiento de la resiliencia urbana. En nuestros análisis, incorporamos de manera proactiva variables científicas críticas como los efectos del cambio climático y tendencias estadísticas avanzadas. Este compromiso nos permite entregar resultados sólidos y proyecciones realistas, diseñados para enfrentar los desafíos presentes y futuros.'
    },
    seo: {
      homeTitle: 'Berrik | Ingeniería Hidráulica y Modelación Automatizada',
      homeDescription: 'Berrik ofrece servicios de ingeniería hidráulica e hidrología avanzada, incluyendo análisis de riesgo de inundaciones, mapas de inundación y modelación automatizada con MiCuenca.',
      aboutTitle: 'Nosotros | Berrik Chile Ingeniería',
      aboutDescription: 'Berrik combina ingeniería civil tradicional con hidroinformática moderna. Especialistas en recursos hídricos, riesgo de inundaciones y modelación en Chile.',
      solutionsTitle: 'Servicios y Soluciones | Berrik Ingeniería',
      solutionsDescription: 'Servicios de ingeniería Berrik: Modelación hidráulica, análisis de riesgo de inundaciones, cargas hidrodinámicas y nuestra herramienta de cuencas, MiCuenca.',
      contactTitle: 'Contacto | Berrik Servicios de Ingeniería',
      contactDescription: 'Contacte a Berrik para ingeniería hidráulica, evaluación de riesgo de inundaciones y consultoría en recursos hídricos. Operamos en Chile e internacionalmente.'
    },
    home: {
      exploreTitle: 'Explora nuestras soluciones y conoce más sobre nosotros',
      exploreSub: 'Descubre cómo combinamos la ingeniería y la automatización para decisiones más rápidas y claras.',
      exploreCtaSolutions: 'Explorar soluciones',
      exploreCtaAbout: 'Sobre nosotros',
      projectsNewsHeader: 'Proyectos y Noticias',
      projectsNewsSub: 'Nuestro trabajo reciente e ideas en hidrología e hidráulica',
      latestUpdatesHeader: 'Últimas Noticias',
      latestUpdatesSub: 'Anuncios y cambios de plataforma para mantenerte informado',
      projectMicuencaTitle: 'MiCuenca (Beta)',
      projectMicuencaDesc: 'MiCuenca está disponible en versión beta. Úsala para análisis preliminar de cuencas.',
      newsBerrikTitle: 'Berrik Entra al Mercado',
      newsBerrikDesc: 'Berrik abre sus puertas, listo para operar y dominar la ingeniería de recursos hídricos.',
      newsOpenClientsTitle: 'Berrik abre sus puertas',
      newsOpenClientsDesc: 'Contáctanos para más información sobre cómo comenzar a operar con nosotros.',
      newsMicuencaRegionsTitle: 'MiCuenca está disponible en Chile de la I a la X región',
      newsMicuencaRegionsDesc: 'Cobertura ampliada desde la Región I a la Región X.',
      newsHiringTitle: 'Berrik se encuentra en búsqueda de nuevos talentos',
      newsHiringDesc: 'Estamos contratando para roles en recursos hídricos y datos.',
      actionViewProjects: 'Ir a MiCuenca.cl',
      actionAboutUs: 'Sobre nosotros',
      meetTeamText: 'Conoce al equipo',
      meetTeamSub: 'Especialistas multidisciplinarios en hidrología, hidráulica e ingeniería de software.',
      meetTeamCta: 'Conoce al equipo',
      careersText: 'Únete para construir infraestructura hídrica resiliente',
      careersSub: 'Buscamos ingenieros y especialistas en datos con foco en impacto e innovación.',
      careersCta: 'Ver vacantes',
      actionViewService: 'Ver servicio relacionado',
      actionReadUpdate: 'Leer más',
      welcome: {
        slide0: {
          title: 'Endure. Optimize. Excel.',
          text: ''
        },
        slide1: {
          title: 'Modelos de Escorrentía Superficial',
          text: 'Modelación hidrológica e hidráulica 1D/2D para análisis de inundaciones con caudales extremos.'
        },
        slide2: {
          title: 'Desarrollo de Herramientas para Análisis de Cuencas',
          text: 'Obtén información en segundos de cuencas y caudales de diseño en MiCuenca.cl.'
        },
        slide3: {
          title: 'Estudios de Ingeniería en Recursos Hídricos',
          text: 'Cambio climático, balance de aguas, permisos sectoriales y ambientales.'
        },
        slide4: {
          title: 'Diseño de Infraestructura Hídrica',
          text: 'Análisis de resiliencia, cargas hidrodinámicas, erosión y socavaciones, '
        }
      }
    },
    team: {
      titles: {
        founderCEO: 'Fundador y CEO',
        coFounder: 'Cofundador',
        seniorEngineer: 'Ingeniero Senior',
        civilDesigner: 'Diseñador Civil'
      },
      names: {
        founder: 'Javier Mardones, PE, MSc',
        coFounder: 'Nombre Apellido',
        seniorEngineer: 'Nombre Apellido',
        civilDesigner: 'Nombre Apellido'
      }
    },
    services: {
      header: 'Servicios de Hidrología e Hidráulica',
      viewDetails: 'Ver detalles',
      requestProposal: 'Solicitar una propuesta',
      solutionsCustom: {
        items: {
          mhh: {
            title: 'Modelación hidrológica e hidráulica',
            description: 'Modelación 1D/2D, calibración y análisis de escenarios para caudales extremos y de diseño.'
          },
          avanzada: {
            title: 'Soluciones de vanguardia en análisis hídrico',
            description: 'Herramientas online de fácil acceso y automatización para optimizar recursos.'
          },
          riesgo: {
            title: 'Análisis de riesgo de inundación',
            description: 'Estudios hidrológicos e hidráulicos que incorporan resiliencia a eventos extremos y cambio climático.'
          },
          permisos: {
            title: 'Evaluación ambiental y permisos sectoriales',
            description: 'Gestión de normativas ambientales y tramitación de permisos dentro del marco regulatorio actual.'
          },
          diseno: {
            title: 'Diseño de obras hidráulicas',
            description: 'Analizamos cargas hidrodinámicas, socavaciones, erosiones, y diseñamos estructuras para resistir eventos extremos.'
          },
          asesoria: {
            title: 'Asesoría de servicios de ingeniería y tecnología',
            description: 'Acompañamiento técnico y soluciones a medida en ingeniería y TI en base a recursos hídricos.'
          }
        }
      }
    },
    pages: {
      solutionsHeader: 'Explora nuestras soluciones',
      updatesHeader: 'Noticias',
      teamHeader: 'Conoce al equipo'
    },
    footer: {
      contactUs: 'Contáctanos',
      rights: 'Todos los derechos reservados.'
    },
    cookies: {
      text: 'Usamos cookies y tecnologías similares para entender el uso y mejorar este sitio. Puedes aceptar o rechazar las cookies no esenciales.',
      accept: 'Aceptar',
      reject: 'Rechazar'
    },
    notFound: {
      title: 'Página no encontrada',
      text: 'La página que buscas no existe o fue movida.',
      cta: 'Volver al inicio'
    },
    contact: {
      header: 'Contacto',
      subheader: '¿Listo para conversar sobre tu proyecto? Escríbenos.',
      infoTitle: 'Información de contacto',
      phoneLabel: 'Teléfono',
      phoneUnavailable: 'Teléfono disponible a solicitud',
      emailLabel: 'Correo electrónico',
      emailResponse: 'Respondemos dentro de 24 horas',
      headOfficeLabel: 'Oficina central',
      officeHoursLabel: 'Horario de atención',
      officeHours: '',
      formTitle: 'Escríbenos',
      nameLabel: 'Nombre *',
      namePlaceholder: 'Tu nombre completo',
      emailFieldLabel: 'Correo electrónico *',
      emailPlaceholder: 'tu.correo@ejemplo.com',
      companyLabel: 'Empresa',
      companyPlaceholder: 'Nombre de tu empresa',
      serviceLabel: 'Servicio',
      servicePlaceholder: 'Elige un servicio',
      serviceOptions: {
        hydraulicModeling: 'Modelación hidrológica e hidráulica',
        sectorPermits: 'Evaluación ambiental y permisos sectoriales',
        hydraulicInfrastructure: 'Diseño de obras hidráulicas',
        engineeringConsult: 'Asesoría de servicios de ingeniería y tecnología',
        other: 'Otro'
      },
      messageLabel: 'Describe tu proyecto *',
      messagePlaceholder: 'Cuéntanos sobre tu proyecto...',
      sendMessage: 'Enviar mensaje',
      sending: 'Enviando...',
      rateLimitPrefix: 'Podrás enviar otro mensaje en',
      rateLimitSuffix: 'min.',
      successMessage: '¡Gracias! Tu mensaje fue enviado con éxito. Te contactaremos pronto.',
      errors: {
        nameRequired: 'El nombre es obligatorio',
        emailRequired: 'El correo es obligatorio',
        emailInvalid: 'El correo no es válido',
        messageRequired: 'El mensaje es obligatorio',
        captchaRequired: 'Completa el captcha.',
        rateLimit: 'Se alcanzó el límite de envíos. Intenta nuevamente más tarde.',
        sendFailed: 'Ocurrió un error al enviar el mensaje.',
        network: 'Error de red. Intenta nuevamente.'
      }
    }
  }
};

const I18nContext = createContext({ lang: 'es', t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState('es');
  useEffect(() => {
    try {
      const cookie = document.cookie || '';
      const match = cookie.split(';').map(s => s.trim()).find(s => s.startsWith('lang='));
      const v = match ? match.split('=')[1] : null;
      if (v === 'es' || v === 'en') setLangState(v);
    } catch {}
  }, []);
  const setLang = (l) => {
    setLangState(l);
    try {
      const oneYear = 365 * 24 * 60 * 60;
      document.cookie = `lang=${l}; path=/; max-age=${oneYear}; samesite=Lax`;
    } catch {}
    try { window.dispatchEvent(new Event('lang-change')); } catch {}
  };
  const t = useMemo(() => {
    const dict = messages[lang] || messages.en;
    return (key) => {
      const parts = key.split('.');
      let cur = dict;
      for (let p of parts) {
        if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
        else return key;
      }
      return typeof cur === 'string' ? cur : key;
    };
  }, [lang]);
  return (
    <I18nContext.Provider value={{ lang, t, setLang }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
